import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import AccountingPlanningActions from "../../AccountingPlanningActions";
import { formatMoney } from "./utils";
import RecognitionsTab from "./RecognitionsTab";
import PeriodSettingsTab from "./PeriodSettingsTab";
import RecognitionDetails from "./RecognitionDetails";
import RecognitionForm from "./RecognitionForm";
import Toast from "./ui/Toast";
import "./ui/buttons.css";
import "./RecognitionPage.css";

const JOURNAL_PATH = "/finances/accounting-planning/operations-journal";

/**
 * Общая страница признания по степени готовности (выручка / расходы).
 * config — тексты, цвет и способ настройки периода (по проекту или по договору),
 * data — справочники { projects, contracts, articles, organizations, counterparties, projectById, contractById }.
 */
function RecognitionPage({ config, data, initialRecognitions, initialSettings }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("recognitions");
  const [recognitions, setRecognitions] = useState(initialRecognitions);
  const [settings, setSettings] = useState(initialSettings);
  const [selectedRecognition, setSelectedRecognition] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [toast, setToast] = useState(null);

  const openJournal = useCallback(() => navigate(JOURNAL_PATH), [navigate]);
  const closeToast = useCallback(() => setToast(null), []);

  const handleRecognize = (draft) => {
    const id = Math.max(0, ...recognitions.map((item) => item.id)) + 1;
    const recognition = {
      ...draft,
      id,
      document: `${config.documentPrefix}-${String(id + 100).padStart(6, "0")}`,
    };
    setRecognitions((prev) => [recognition, ...prev]);
    setIsFormOpen(false);
    setToast({
      id,
      title: config.texts.toastTitle,
      text: `Сформирована операция на ${formatMoney(recognition.current)}.`,
      actionLabel: "Открыть в Журнале операций",
      onAction: openJournal,
    });
  };

  const tabs = [
    { id: "recognitions", label: "Признания" },
    { id: "settings", label: config.texts.settingsTab },
  ];

  return (
    <div className={`rr ${config.themeClass}`}>
      {activeTab === "recognitions" && (
        <AccountingPlanningActions>
          <button type="button" className="rr-btn rr-btn--primary" onClick={() => setIsFormOpen(true)}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 3.333V12.667M3.333 8H12.667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            Создать признание
          </button>
        </AccountingPlanningActions>
      )}

      <div className="rr-card">
        <div className="rr-tabs" role="tablist">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`rr-tabs__tab${activeTab === tab.id ? " is-active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === "recognitions" ? (
          <RecognitionsTab
            config={config}
            data={data}
            recognitions={recognitions}
            selectedId={selectedRecognition?.id}
            onSelect={setSelectedRecognition}
          />
        ) : (
          <PeriodSettingsTab config={config} data={data} settings={settings} onChange={setSettings} />
        )}
      </div>

      <RecognitionDetails
        config={config}
        data={data}
        recognition={selectedRecognition}
        onClose={() => setSelectedRecognition(null)}
        onOpenJournal={openJournal}
      />

      <RecognitionForm
        config={config}
        data={data}
        isOpen={isFormOpen}
        recognitions={recognitions}
        settings={settings}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleRecognize}
      />

      <Toast toast={toast} onClose={closeToast} />
    </div>
  );
}

export default RecognitionPage;
